import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  sortModGroups,
  getNestedModGroupsForSelectedModGroup,
} from '../utils/menu';
import { CartItem, CartModifierGroup } from '../utils/cart';
import {
  ParsedMenuItem,
  ParsedModifierGroup,
  SlashCommand,
} from '../types/menu';
import { selectRestaurant, selectStage } from './restaurantSlice';
import { selectMenuVersion } from './menuSlice';
import { orderActions } from './orderSlice';
import { sendHITLSessionEnd } from './messagingSlice';

// Navigation level constants
export const NAVIGATION_LEVELS = {
  TOP: 'top',
  NESTED: 'nested',
} as const;

export type NavigationLevel =
  (typeof NAVIGATION_LEVELS)[keyof typeof NAVIGATION_LEVELS];

export interface DialogState {
  step: number;
  steps: string[];
  slashCommands: SlashCommand[];
  slashCommandIndex: number;
  selectedItem: CartItem | undefined;
  selectedItemCartId: number;
  invalidModGroupDescriptions: string[];
  voiceBoardValue: string;
  intentsStatus: Intent[];
  nextSelectedModGroupIndex: number;
  selectedModGroup?: ParsedModifierGroup;
  selectedModGroupPath?: string; // Unique path to the selected modifier group
  highlightedModifier?: string; // Highlighted in Omnibar search result
  clickedModifier?: string; // Path to the clicked modifier for visual feedback
  // Navigation context for hierarchical modifier group navigation
  navigationLevel: NavigationLevel;
  parentModGroupId?: string; // ID of the parent modifier group when in nested level
}

interface Intent {
  clickable: boolean;
  lastMessageIndex: number | undefined;
}
const initialState: DialogState = {
  step: 0,
  selectedItem: undefined,
  selectedItemCartId: 0,
  invalidModGroupDescriptions: [],
  voiceBoardValue: '',
  steps: ['What can I get for you today?', 'Sure thing!'],
  slashCommands: [],
  slashCommandIndex: 0,
  intentsStatus: [],
  nextSelectedModGroupIndex: 0,
  selectedModGroupPath: undefined,
  clickedModifier: undefined,
  navigationLevel: NAVIGATION_LEVELS.TOP,
  parentModGroupId: undefined,
};
const dialogSlice = createSlice({
  name: 'dialog',
  initialState: initialState,
  reducers: {
    updateSelectedItem: (
      state,
      action: PayloadAction<{ item: CartItem; itemCartId: number } | undefined>
    ) => {
      if (action.payload) {
        state.selectedItem = action.payload.item;
        state.selectedItemCartId = action.payload.itemCartId;
      } else {
        state.selectedItem = undefined;
        state.selectedItemCartId = 0;
      }
    },
    cloneSelectedItem: (
      state,
      action: PayloadAction<{ cartItemId: number }>
    ) => {
      state.selectedItemCartId = action.payload.cartItemId;
      state.nextSelectedModGroupIndex = 0;
      state.selectedModGroup = undefined;
      state.selectedModGroupPath = undefined;
      state.clickedModifier = undefined;
      state.navigationLevel = NAVIGATION_LEVELS.TOP;
      state.parentModGroupId = undefined;
    },
    resetDialogSelectedItem: (state) => {
      state.selectedItem = undefined;
      state.selectedItemCartId = 0;
    },
    selectItemAndResetNavigation: (
      state,
      action: PayloadAction<{ item: CartItem; itemCartId: number } | undefined>
    ) => {
      // Update selected item
      if (action.payload) {
        state.selectedItem = action.payload.item;
        state.selectedItemCartId = action.payload.itemCartId;
      } else {
        state.selectedItem = undefined;
        state.selectedItemCartId = 0;
      }

      // Reset all navigation-related state
      state.nextSelectedModGroupIndex = 0;
      state.selectedModGroup = undefined;
      state.selectedModGroupPath = undefined;
      state.clickedModifier = undefined;
      state.navigationLevel = NAVIGATION_LEVELS.TOP;
      state.parentModGroupId = undefined;
    },
    addInvalidModGroupDescriptions: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.invalidModGroupDescriptions.concat(action.payload);
    },
    increaseStep: (state) => {
      state.step += 1;
    },
    setStep: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
    setSteps: (state, action: PayloadAction<string[]>) => {
      state.steps = action.payload;
    },
    setVoiceBoardValue: (state, action: PayloadAction<string>) => {
      state.voiceBoardValue = action.payload;
    },
    initIntentsStatus: (state, action: PayloadAction<Intent[]>) => {
      state.intentsStatus = action.payload;
    },
    setIntentClickable: (
      state,
      action: PayloadAction<{ idx: number; status: boolean }>
    ) => {
      state.intentsStatus[action.payload.idx].clickable = action.payload.status;
    },
    setIntentLastMessageIdx: (
      state,
      action: PayloadAction<{ idx: number; lastMessageIndex: number }>
    ) => {
      state.intentsStatus[action.payload.idx].lastMessageIndex =
        action.payload.lastMessageIndex;
    },
    updateSelectedModGroup: (
      state,
      action: PayloadAction<{
        modGroup: ParsedModifierGroup | CartModifierGroup | undefined;
        pathToItem?: string;
      }>
    ) => {
      const { modGroup: selectedModGroup, pathToItem } = action.payload;
      if (selectedModGroup && state.selectedItem) {
        state.selectedModGroup = selectedModGroup;
        state.selectedModGroupPath = pathToItem;

        let modGroupsToSearch: ParsedModifierGroup[] = [];

        if (state.navigationLevel === NAVIGATION_LEVELS.TOP) {
          // Search in top-level modifier groups
          modGroupsToSearch = sortModGroups(state.selectedItem);
        } else if (
          state.navigationLevel === NAVIGATION_LEVELS.NESTED &&
          state.parentModGroupId
        ) {
          // Search in nested modifier groups for the current parent
          modGroupsToSearch = getNestedModGroupsForSelectedModGroup(
            state.selectedItem,
            state.parentModGroupId
          );
        }

        const currentSelectedModGroupIndex = modGroupsToSearch.findIndex(
          (modGroup) => modGroup.id === selectedModGroup.id
        );
        state.nextSelectedModGroupIndex =
          currentSelectedModGroupIndex > -1
            ? currentSelectedModGroupIndex + 1
            : 0;
      } else {
        state.selectedModGroup = undefined;
        state.selectedModGroupPath = undefined;
        state.nextSelectedModGroupIndex = 0;
      }
    },
    setSelectedModGroupIndex: (state, action: PayloadAction<number>) => {
      state.nextSelectedModGroupIndex = action.payload;
    },
    setHighlightedModifier: (state, action: PayloadAction<string>) => {
      state.highlightedModifier = action.payload;
    },
    addDialogStep: (
      state,
      action: PayloadAction<{ step: string; index?: number }>
    ) => {
      const { step, index } = action.payload;
      const insertIndex = index !== undefined ? index : state.step;
      state.steps.splice(insertIndex, 0, step);
    },
    addSlashCommands: (state, action: PayloadAction<SlashCommand[]>) => {
      state.slashCommands = action.payload;
      state.slashCommandIndex = 0; // Reset index when new slash commands are added
    },
    nextSlashCommand: (state) => {
      if (state.slashCommandIndex < state.slashCommands.length - 1) {
        state.slashCommandIndex += 1;
      } else {
        // When at the last slash command, advance beyond the array to trigger fallback
        state.slashCommandIndex = state.slashCommands.length;
      }
    },
    // Navigate deeper into nested modifier groups
    navigateToNestedLevel: (
      state,
      action: PayloadAction<{
        parentModGroupId: string;
        firstNestedModGroup?: ParsedModifierGroup;
        firstNestedModGroupPath?: string;
      }>
    ) => {
      const { firstNestedModGroupPath, firstNestedModGroup, parentModGroupId } =
        action.payload || {};
      state.navigationLevel = NAVIGATION_LEVELS.NESTED;
      state.parentModGroupId = parentModGroupId;
      state.nextSelectedModGroupIndex = 1; // Set to 1 since we're selecting the first item
      state.selectedModGroup = firstNestedModGroup;
      state.selectedModGroupPath = firstNestedModGroupPath;
    },
    // Navigate back to parent level
    navigateToParentLevel: (
      state,
      action: PayloadAction<{
        parentModGroup?: ParsedModifierGroup;
        parentModGroupPath?: string;
      }>
    ) => {
      const { parentModGroup, parentModGroupPath } = action.payload || {};
      state.navigationLevel = NAVIGATION_LEVELS.TOP;
      state.parentModGroupId = undefined;

      if (parentModGroup && state.selectedItem) {
        // Find the index of the parent modifier group in top-level groups
        const topLevelModGroups = sortModGroups(state.selectedItem);
        const parentIndex = topLevelModGroups.findIndex(
          (modGroup) => modGroup.id === parentModGroup.id
        );

        state.nextSelectedModGroupIndex =
          parentIndex > -1 ? parentIndex + 1 : 0;
        state.selectedModGroup = parentModGroup;
        state.selectedModGroupPath = parentModGroupPath;
      } else {
        state.nextSelectedModGroupIndex = 0;
        state.selectedModGroup = undefined;
        state.selectedModGroupPath = undefined;
      }
    },
    setClickedModifier: (state, action: PayloadAction<string | undefined>) => {
      state.clickedModifier = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Reset slash commands state on HITL session end
    builder.addCase(sendHITLSessionEnd, (state) => {
      state.slashCommands = [];
      state.slashCommandIndex = 0;
    });
    builder.addMatcher(
      isAnyOf(
        selectRestaurant.fulfilled,
        selectStage.fulfilled,
        selectMenuVersion.fulfilled
      ),
      () => {
        return initialState;
      }
    );
  },
});

export const dialogActions = dialogSlice.actions;
export const {
  resetDialogSelectedItem,
  selectItemAndResetNavigation,
  addDialogStep,
  setSteps,
  addSlashCommands,
  nextSlashCommand,
} = dialogActions;
export default dialogSlice.reducer;
